import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Search, ArrowRight, ChevronLeft, Phone, Spade } from 'lucide-react';
import { USE_GROUPS, OCCASIONS, OCCASION_FILTERS } from '../data/drivenOccasions';

const DrivenOccasions = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const filterTabs = useMemo(() => [
    { key: 'all', label: 'All Occasions' },
    ...USE_GROUPS.map(g => ({ key: g.key, label: g.label.split(' &')[0] })),
  ], []);

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    return USE_GROUPS
      .filter(g => activeFilter === 'all' || g.key === activeFilter)
      .map(g => ({ ...g, cards: g.cards.filter(c => !q || (c.title + c.desc + c.tag).toLowerCase().includes(q)) }))
      .filter(g => g.cards.length > 0);
  }, [search, activeFilter]);

  const totalCount = USE_GROUPS.reduce((s, g) => s + g.cards.length, 0);

  return (
    <div className="bg-white min-h-screen" data-testid="driven-occasions-page">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-black via-gray-950 to-black text-white py-20 sm:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,_rgba(220,38,38,0.15),_transparent_60%)]" />
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <button onClick={() => navigate('/driven')} className="inline-flex items-center text-xs text-gray-400 hover:text-white mb-6 transition-colors" data-testid="back-to-driven">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Driven
          </button>
          <p className="text-red-500 text-xs font-bold tracking-[0.3em] uppercase mb-4">Occasions & Use Cases</p>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-[1.05] tracking-tight max-w-4xl mb-4" data-testid="occasions-h1">
            Every Reason <br />
            <em className="not-italic text-red-500">to Ride.</em>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl leading-relaxed mb-8">
            From the airport run on Monday morning to the wedding getaway on Saturday night. Browse the full catalog of {totalCount} use-case chapters and {OCCASIONS.length}+ specific occasions, or jump straight to what you need.
          </p>

          {/* Search */}
          <div className="relative max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search occasions — wedding, airport, golf, concert…"
              className="pl-10 h-12 bg-gray-900 border-gray-800 text-white text-sm focus:border-red-500"
              data-testid="occasions-search"
            />
          </div>
        </div>
      </section>

      {/* Filter chip bar (sticky) */}
      <div className="sticky top-16 sm:top-20 z-30 bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 py-3 flex gap-2 overflow-x-auto" data-testid="filter-tabs">
          {filterTabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveFilter(t.key)}
              className={`text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-full border whitespace-nowrap transition-all ${
                activeFilter === t.key
                  ? 'bg-red-600 text-white border-red-600 shadow'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
              data-testid={`filter-${t.key}`}
            >{t.label}</button>
          ))}
        </div>
      </div>

      {/* Use-case Groups */}
      {filteredGroups.length === 0 ? (
        <section className="py-24 text-center">
          <p className="text-sm text-gray-500">No occasions match "{search}". Try a different keyword or reset filters.</p>
        </section>
      ) : (
        filteredGroups.map((g, gi) => (
          <section key={g.key} className="py-14 sm:py-20" data-testid={`occasion-group-${g.key}`}>
            <div className="max-w-7xl mx-auto px-6">
              <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3 flex items-center gap-2">
                <g.icon className="h-3.5 w-3.5" /> {g.num} · {g.label.toUpperCase()}
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl">{g.title}</h2>
              <p className="text-sm sm:text-base text-gray-500 mt-4 max-w-2xl leading-relaxed">{g.desc}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
                {g.cards.map((c, ci) => {
                  const Icon = c.icon;
                  return (
                    <Card key={ci} className="p-6 border-gray-200 hover:border-red-200 hover:shadow-lg transition-all group" data-testid={`occasion-card-${g.key}-${ci}`}>
                      <div className="h-10 w-10 rounded-lg bg-red-50 flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors">
                        <Icon className="h-5 w-5 text-red-600 group-hover:text-white transition-colors" />
                      </div>
                      <p className="text-[10px] font-bold uppercase tracking-widest text-red-600 mb-1.5">{c.tag}</p>
                      <h3 className="text-base sm:text-lg font-bold text-black mb-2">{c.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">{c.desc}</p>
                      <span className="inline-block text-[10px] font-semibold uppercase tracking-wider text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">{c.vehicles}</span>
                    </Card>
                  );
                })}
              </div>
            </div>
            {gi < filteredGroups.length - 1 && <div className="max-w-7xl mx-auto px-6 mt-14"><div className="h-px bg-gradient-to-r from-transparent via-red-600/40 to-transparent" /></div>}
          </section>
        ))
      )}

      {/* THE FULL PICTURE — chip cloud of every occasion */}
      <section className="bg-gray-50 py-14 sm:py-20 border-t border-gray-200" id="all-occasions">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-[11px] text-red-600 font-bold tracking-[0.25em] mb-3">THE FULL PICTURE</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.1] max-w-3xl mb-3">
            If You're Going,<br /><em className="not-italic text-red-500">ACE Can Drive.</em>
          </h2>
          <p className="text-sm text-gray-500 max-w-2xl leading-relaxed mb-8">Every supported occasion at a glance — {OCCASIONS.length} use cases categorized for fast browsing. Tap any chip to start a booking.</p>

          <FullOccasionsChips />
        </div>
      </section>

      {/* CTA Band */}
      <section className="bg-gradient-to-br from-black via-gray-950 to-black text-white py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-4 tracking-tight">Don't see your occasion? <em className="text-red-500 not-italic">Just ask.</em></h2>
          <p className="text-sm sm:text-base text-gray-400 max-w-2xl mx-auto mb-8">If you can imagine it, we can drive it. ACE handles custom multi-day itineraries, executive accounts, and bespoke transportation scenarios most companies won't touch.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/booking?service=concierge')} className="bg-red-600 hover:bg-red-700 text-white text-sm h-12 px-7" data-testid="occasions-cta-book">
              Book a Concierge Ride <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
            <a href="tel:6788463009" className="inline-flex items-center justify-center text-sm h-12 px-7 rounded-lg border border-gray-700 text-gray-300 hover:bg-gray-900" data-testid="occasions-cta-call">
              <Phone className="h-4 w-4 mr-2" /> (678) 846-3009
            </a>
          </div>
          <p className="text-[11px] text-gray-500 mt-6">
            See <button onClick={() => navigate('/rates')} className="text-red-400 underline hover:text-red-300">Rates →</button> · <button onClick={() => navigate('/driven')} className="text-red-400 underline hover:text-red-300">Driven Standards →</button>
          </p>
        </div>
      </section>
    </div>
  );
};

export default DrivenOccasions;

/* ─────────── FULL OCCASIONS CHIP CLOUD ─────────── */
const FullOccasionsChips = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return OCCASIONS.filter(o => {
      const matchSearch = !q || o.name.toLowerCase().includes(q);
      const matchFilter = filter === 'all' || (o.cats || []).includes(filter);
      return matchSearch && matchFilter;
    });
  }, [search, filter]);

  return (
    <>
      <div className="relative max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search any occasion…"
          className="pl-9 h-11 bg-white border-gray-200"
          data-testid="full-occasions-search"
        />
      </div>

      <div className="flex flex-wrap gap-2 mb-8" data-testid="full-occasions-filters">
        {OCCASION_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full transition-all ${
              filter === f.key ? 'bg-black text-white shadow-md' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
            }`}
            data-testid={`full-filter-${f.key}`}
          >{f.label}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center" data-testid="full-no-results">
          <p className="text-sm text-gray-500">
            No occasions found — but if you're going somewhere, ACE can drive.{' '}
            <button onClick={() => navigate('/booking')} className="text-red-600 font-bold underline">Just ask us.</button>
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2.5" data-testid="full-occasions-grid">
          {filtered.map((o) => (
            <button
              key={o.name}
              onClick={() => navigate('/booking')}
              className="text-left group bg-white border border-gray-200 hover:border-red-400 hover:shadow-md transition-all rounded-lg px-3.5 py-3 flex items-center gap-2.5"
              data-testid={`full-occasion-${o.name.replace(/\W+/g, '-').toLowerCase()}`}
            >
              <Spade className="h-3.5 w-3.5 text-red-500 flex-shrink-0 group-hover:scale-110 transition-transform" />
              <span className="text-sm font-medium text-gray-800 group-hover:text-red-600 transition-colors truncate">{o.name}</span>
            </button>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-500 mt-6 text-center" data-testid="full-occasions-count">
        Showing {filtered.length} of {OCCASIONS.length} occasions
      </p>
    </>
  );
};
